import React, { useState } from "react";
import "./imageGroups.css";
import AdminPictureUpload from "../../adminComponents/AdminPictureUpload/AdminPicUpload";
import FinestJobsController from "../../adminComponents/FinestJobsControl/FinestJobsController";
import DeleteBAGroup from "../../adminComponents/DeleteBagGroupDropdown/DeleteBagGroupComp";
import SinglePictureLookup from "../../adminComponents/SinglePictureLookup/SinglePictureLookup";
import RecentBAGsDropdown from "../../adminComponents/RecentBAGsDropdown/RecentBAGsDropdown";
import GalleryPairs from "../../adminComponents/GalleryPairs/GalleryPairs";
import Auth from "../../../../utils/auth";
import { hasRole } from "../../../../utils/permissions";

/**
 * Image Groups — a TAB page, matching Business Ledger.
 *
 * ─────────────────────────────────────────────────────────────────
 * IT WAS FIVE STACKED DISCLOSURES. Every section was collapsed by default, so
 * the page opened as five bars and nothing else, and reaching any one of them
 * was a click that pushed the other four down. A tab strip is the same choice
 * made once, in the header, with the chosen section already open — and only
 * one section is ever useful at a time here anyway.
 *
 * The sections themselves are unchanged. Each takes `inPanel`, which drops its
 * own gradient shell (the page supplies the panel) and renders its
 * `BdrDisclosure` in `bare` mode — content only, no bar to click, because the
 * tab above IS that control.
 *
 * ─────────────────────────────────────────────────────────────────
 * WHAT WENT WITH IT: a `handlePageHeight` that summed five `offsetHeight`
 * reads behind a 1000ms `setTimeout` and applied the total as `minHeight`. It
 * had two bugs — it read `#delete-ba-group-height-marker` TWICE (so Delete was
 * counted double and Photo Lookup never), and it summed `#image-page-header`,
 * an id that exists nowhere, which made the total `NaN`. It also added a whole
 * `window.innerHeight` on top, leaving roughly a viewport of empty scroll
 * below the content. The panel body scrolls itself now, so there is no height
 * to compute: the fourth hand-rolled viewport measurement in this app, and the
 * only one that was also wrong.
 */

/* `adminOnly` hides the tab from everyone else. It is presentation only: the
   tab's query (getGalleryPairs) enforces the admin role on the server. */
const TABS = [
  { key: "create", label: "Create B&A Group" },
  { key: "finest", label: "Finest Carousel" },
  { key: "recent", label: "Recent BAGs" },
  { key: "pairs", label: "Gallery Pairs", adminOnly: true },
  { key: "lookup", label: "Photo Lookup" },
  { key: "delete", label: "Delete BA Group" },
];

const AdminImageGroups = () => {
  /* Create is first and default: it is the one section people come here to
     use. Delete is last for the same reason, in reverse. */
  const [activeTab, setActiveTab] = useState("create");
  const [newGroupId, setNewGroupId] = useState(false);

  const isAdmin = (() => {
    try {
      const { data: profile } = Auth.getProfile();
      return hasRole(profile?.permission, "admin");
    } catch {
      return false;
    }
  })();
  const visibleTabs = TABS.filter((tab) => !tab.adminOnly || isAdmin);

  return (
    <div className="ig-page">
      <div className="ig-container bdr-section-panel bdr-dark-placeholder">
        {/* Title and tabs share the blue band: the tabs choose what the body
            shows, which is header business. Same shape as Business Ledger. */}
        <div className="bdr-section-panel-hdr ig-header">
          <h2 className="bdr-section-panel-title">Image Groups</h2>

          <div className="ig-tabs">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`ig-tab${
                  activeTab === tab.key ? " ig-tab-active" : ""
                }`}
                aria-pressed={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/*
          The gray body. It is NOT `--scroll`: it grows to its content and
          the document scrolls past it. See the stylesheet's note — an inner
          scroller is for a long list, not for a form.

          Each section is MOUNTED ONLY WHILE SELECTED — these are heavy
          (uploads, image grids, their own queries) and keeping four of them
          alive behind a hidden tab would run four sets of fetches for one
          visible section.
        */}
        <div className="bdr-section-panel-body ig-body">
          {activeTab === "create" && (
            <AdminPictureUpload inPanel handleNewGroupId={setNewGroupId} />
          )}
          {activeTab === "finest" && (
            <FinestJobsController inPanel newGroupId={newGroupId} />
          )}
          {activeTab === "recent" && <RecentBAGsDropdown inPanel />}
          {activeTab === "pairs" && isAdmin && <GalleryPairs />}
          {activeTab === "lookup" && <SinglePictureLookup inPanel />}
          {activeTab === "delete" && <DeleteBAGroup inPanel />}
        </div>
      </div>
    </div>
  );
};

export default AdminImageGroups;
